import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI, statsAPI, ordersAPI, servicesAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { 
  User, 
  Mail, 
  Edit, 
  Save, 
  X,
  TrendingUp,
  TrendingDown,
  Clock,
  ShoppingBag,
  Package,
  Plus
} from 'lucide-react';

export const ProfilePage = () => {
  const { user, updateUser, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [receivedOrders, setReceivedOrders] = useState([]);
  const [placedOrders, setPlacedOrders] = useState([]);
  const [profileData, setProfileData] = useState({
    name: '',
    bio: '',
    skills: []
  });
  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        bio: user.bio || '',
        skills: user.skills || []
      });
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [transactionsRes, receivedRes, placedRes] = await Promise.all([
        statsAPI.getTransactions(20),
        ordersAPI.getReceived(),
        ordersAPI.getPlaced()
      ]);
      setTransactions(transactionsRes.data);
      setReceivedOrders(receivedRes.data);
      setPlacedOrders(placedRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await authAPI.updateProfile(profileData);
      updateUser(profileData);
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !profileData.skills.includes(newSkill.trim())) {
      setProfileData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skill) => {
    setProfileData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Profile Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            <Avatar className="w-24 h-24">
              <AvatarImage src={user?.avatar_url} alt={user?.name} />
              <AvatarFallback className="bg-indigo-100 text-indigo-600 text-3xl font-bold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1">
              {editing ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={profileData.name}
                      onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bio</Label>
                    <Textarea
                      placeholder="Tell us about yourself..."
                      value={profileData.bio}
                      onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                      data-testid="profile-bio-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Skills</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {profileData.skills.map((skill, index) => (
                        <Badge key={index} variant="secondary" className="gap-1">
                          {skill}
                          <button onClick={() => handleRemoveSkill(skill)} className="ml-1">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a skill..."
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                        data-testid="profile-skill-input"
                      />
                      <Button type="button" variant="outline" onClick={handleAddSkill}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSave} disabled={loading} data-testid="save-profile-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button variant="outline" onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between">
                    <div>
                      <h1 className="font-heading font-bold text-2xl text-slate-900">{user?.name}</h1>
                      <p className="text-slate-600 flex items-center gap-2 mt-1">
                        <Mail className="w-4 h-4" />
                        {user?.email}
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                  
                  {user?.bio && (
                    <p className="text-slate-700 mt-4">{user.bio}</p>
                  )}
                  
                  {user?.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {user.skills.map((skill, index) => (
                        <Badge key={index} variant="secondary">{skill}</Badge>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Points Display */}
            <Card className="gradient-secondary text-white w-full sm:w-auto sm:min-w-[180px]">
              <CardContent className="py-4 text-center">
                <p className="text-orange-100 text-sm">Total Points</p>
                <p className="font-impact text-4xl">{user?.points?.toLocaleString() || 0}</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transactions" data-testid="transactions-tab">Points History</TabsTrigger>
          <TabsTrigger value="received" data-testid="received-orders-tab">Orders Received ({receivedOrders.length})</TabsTrigger>
          <TabsTrigger value="placed" data-testid="placed-orders-tab">Orders Placed ({placedOrders.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Points History</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No transactions yet</p>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div 
                      key={tx.id} 
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200"
                      data-testid={`transaction-${tx.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.type === 'earn' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {tx.type === 'earn' ? (
                            <TrendingUp className="w-5 h-5 text-green-600" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{tx.description}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(tx.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className={`font-impact text-xl ${
                        tx.type === 'earn' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {tx.type === 'earn' ? '+' : '-'}{tx.amount}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="received">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                Orders Received
              </CardTitle>
            </CardHeader>
            <CardContent>
              {receivedOrders.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No orders received yet</p>
              ) : (
                <div className="space-y-3">
                  {receivedOrders.map((order) => (
                    <div 
                      key={order.id} 
                      className="p-4 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{order.service_title}</p>
                          <p className="text-sm text-slate-600">From: {order.buyer_name}</p>
                          {order.message && (
                            <p className="text-sm text-slate-600 mt-2 p-2 bg-slate-50 rounded">
                              "{order.message}"
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-100 text-green-700 font-impact">
                            +{order.price_points} PTS
                          </Badge>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="placed">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Orders Placed
              </CardTitle>
            </CardHeader>
            <CardContent>
              {placedOrders.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No orders placed yet</p>
              ) : (
                <div className="space-y-3">
                  {placedOrders.map((order) => (
                    <div 
                      key={order.id} 
                      className="p-4 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{order.service_title}</p>
                          <p className="text-sm text-slate-600">Provider: {order.provider_name}</p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-orange-100 text-orange-700 font-impact">
                            -{order.price_points} PTS
                          </Badge>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;
